import {
  GenericGridItem,
  WbCard,
  WbButton,
  WbCardContent,
  WbGenericFieldGrid,
} from '@agilelab/plugin-wb-platform';
import { Box, makeStyles, useTheme } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { DateTime } from 'luxon';
import { FC, useCallback } from 'react';
import { NewEditRoleDrawer } from '../RoleDrawer';
import { RoleFormType } from '../RoleForm';
import { RolePermissionsDrawer } from './ManageRolePermissionsDrawer';
import { useOverviewContext } from './OverviewContext';
import {
  DeleteConfirmDialog,
  RolePermissionCardHeader,
  RolePermissionsTable,
} from './RolePermissionsTable';
import { RolePermissionsTableContextProvider } from './RolePermissionsTableContext';

const useStyles = makeStyles(
  theme => ({
    container: {
      height: '100%',
      display: 'flex',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    flexBox: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    scrollableTable: {
      display: 'flex',
      flexDirection: 'column',
      paddingLeft: theme.spacing(2),
      paddingTop: theme.spacing(1),
      height: '100%',
      overflow: 'hidden',
    },
  }),
  { name: 'OverviewTab' },
);

const GeneralInfoCardActions: FC = () => {
  const {
    openDrawer,
    setOpenDrawer,
    error,
    roleState,
    editRole,
    editRoleState,
    canEditRolePermission,
  } = useOverviewContext();

  const onSubmit = useCallback(
    (newValues: RoleFormType) => {
      const valuesToSubmit: RoleFormType = { ...newValues };

      if (!valuesToSubmit.description?.length)
        valuesToSubmit.description = undefined;

      editRole(valuesToSubmit);
    },
    [editRole],
  );
  return (
    canEditRolePermission &&
    roleState.value?.visibility !== 'internal' && (
      <>
        <WbButton
          label="Edit Role"
          startIcon={<EditIcon />}
          onClick={() => setOpenDrawer(true)}
          color="secondary"
          size="small"
          variant="outlined"
        />
        <NewEditRoleDrawer
          defaultValues={{
            displayName: roleState.value?.displayName!,
            description: roleState.value?.description ?? '',
          }}
          onSubmit={onSubmit}
          dataState={editRoleState}
          openDrawer={openDrawer}
          setOpenDrawer={setOpenDrawer}
          error={error}
        />
      </>
    )
  );
};

const GeneralInfoBox = () => {
  const classes = useStyles();

  const { roleState } = useOverviewContext();

  const fields: GenericGridItem[] = [
    {
      label: 'Description',
      value: roleState.value?.description,
      colSpan: 4,
    },
    {
      label: 'Created By',
      value: roleState.value?.createdBy,
      colSpan: { sm: 1, xs: 4 },
    },
    {
      label: 'Created At',
      value: DateTime.fromISO(
        roleState?.value?.createdAt?.toString() || '',
      ).toFormat('dd/MM/yyyy HH:mm'),
      colSpan: { sm: 1, xs: 4 },
    },
    {
      label: 'Updated By',
      value: roleState.value?.updatedBy,
      colSpan: { sm: 1, xs: 4 },
    },
    {
      label: 'Updated At',
      value: DateTime.fromISO(
        roleState?.value?.updatedAt?.toString() || '',
      ).toFormat('dd/MM/yyyy HH:mm'),
      colSpan: { sm: 1, xs: 4 },
    },
  ];

  return (
    <WbCard title="General Information" actions={<GeneralInfoCardActions />}>
      <WbCardContent className={classes.container}>
        <WbGenericFieldGrid
          fields={fields}
          columns={4}
          borderBetweenRows={false}
        />
      </WbCardContent>
    </WbCard>
  );
};

export const OverviewTab: FC = () => {
  const theme = useTheme();
  const classes = useStyles();
  return (
    <Box
      display="flex"
      flexDirection="column"
      style={{ gap: theme.spacing(2) }}
    >
      <GeneralInfoBox />
      <RolePermissionsTableContextProvider>
        <WbCard title="Permissions" cardStyle={{ flex: 1, overflow: 'hidden' }}>
          <WbCardContent className={classes.scrollableTable}>
            <RolePermissionCardHeader />
            <RolePermissionsTable />
          </WbCardContent>
        </WbCard>
        <DeleteConfirmDialog />
        <RolePermissionsDrawer />
      </RolePermissionsTableContextProvider>
    </Box>
  );
};
